import React, { useState, useRef } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import ValidationFeedback from '@/components/ui/ValidationFeedback';

const ResumeUploadStep = ({ onFileSelect, selectedFile, error }) => {
const [isDragging, setIsDragging] = useState(false);
const fileInputRef = useRef(null);

const handleDragEnter = (e) => {
e?.preventDefault();
e?.stopPropagation();
setIsDragging(true);
};

const handleDragLeave = (e) => {
e?.preventDefault();
e?.stopPropagation();
setIsDragging(false);
};

const handleDragOver = (e) => {
e?.preventDefault();
e?.stopPropagation();
};

const handleDrop = (e) => {
e?.preventDefault();
e?.stopPropagation();
setIsDragging(false);

const files = e?.dataTransfer?.files;
if (files && files?.length > 0) {
handleFileSelection(files?.[0]);
}
};

const handleFileSelection = (file) => {
if (!file) return;

if (file?.type !== 'application/pdf') {
onFileSelect(null, 'Please upload a PDF file only');
return;
}

const maxSize = 10 * 1024 * 1024;
if (file?.size > maxSize) {
onFileSelect(null, 'File size must be less than 10MB');
return;
}

onFileSelect(file, null);
};

const handleFileInputChange = (e) => {
const file = e?.target?.files?.[0];
if (file) {
handleFileSelection(file);
}
};

const handleBrowseClick = () => {
fileInputRef?.current?.click();
};

const handleRemoveFile = () => {
onFileSelect(null, null);
if (fileInputRef?.current) {
fileInputRef.current.value = '';
}
};

const formatFileSize = (bytes) => {
if (bytes === 0) return '0 Bytes';
const k = 1024;
const sizes = ['Bytes', 'KB', 'MB'];
const i = Math.floor(Math.log(bytes) / Math.log(k));
return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
};

return (
<div className="space-y-6">
<div className="step-form-section">
<div className="mb-4">
<h2 className="step-form-section-title">Upload Your Resume</h2>
<p className="step-description">Start by uploading your professional resume</p>
</div>


<div
className={`file-upload-zone ${isDragging ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
onDragEnter={handleDragEnter}
onDragLeave={handleDragLeave}
onDragOver={handleDragOver}
onDrop={handleDrop}
>
<input
ref={fileInputRef}
type="file"
accept=".pdf"
onChange={handleFileInputChange}
className="hidden"
aria-label="Resume file upload"
/>

{!selectedFile ? (
<>
<Icon name="Upload" size={48} className="file-upload-icon" />
<p className="file-upload-text">
Drag and drop your resume here, or click to browse
</p>
<p className="file-upload-hint">
PDF format only • Maximum size: 10MB
</p>
<Button
variant="outline"
onClick={handleBrowseClick}
iconName="FileText"
iconPosition="left"
className="mt-4"
>
Browse Files
</Button>
</>
) : (
<div className="file-preview">
<Icon name="FileCheck" size={24} className="file-preview-icon" />
<div className="file-preview-info">
<p className="file-preview-name">{selectedFile?.name}</p>
<p className="file-preview-size">{formatFileSize(selectedFile?.size)}</p>
</div>
<Button
variant="ghost"
size="icon"
onClick={handleRemoveFile}
iconName="X"
aria-label="Remove file"
/>
</div>
)}
</div>

{error && (
<ValidationFeedback type="error" message={error} className="mt-4" />
)}

<div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
<div className="flex items-start gap-3">
<Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
<div className="text-sm text-blue-900 dark:text-blue-100">
<p className="font-medium mb-1">Resume Requirements:</p>
<ul className="list-disc list-inside space-y-1 text-blue-800 dark:text-blue-200">
<li>Must be in PDF format</li>
<li>File size should not exceed 10MB</li>
<li>Should include your work experience and education</li>
<li>Contact information should be clearly visible</li>
</ul>
</div>
</div>
</div>
</div>
</div>
);
};

export default ResumeUploadStep;